import { lazy, Suspense, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Badge,
  Button,
  Banner,
  ProgressBar,
  BlockStack,
  InlineGrid,
  InlineStack,
  Icon,
  SkeletonBodyText,
  SkeletonDisplayText,
  Divider,
  Box,
} from '@shopify/polaris';
import { CheckCircleIcon, AlertCircleIcon } from '@shopify/polaris-icons';
import { useSubscription } from '../hooks/useSubscription';
import { useUsage } from '../hooks/useUsage';
import { useSettings } from '../hooks/useSettings';
import { useThemeCompatibility } from '../hooks/useThemeCompatibility';
import ReviewPromptBanner from '../components/ReviewPromptBanner';
import TrialQuotaBanner from '../components/TrialQuotaBanner';
import SizeCoverageBanner from '../components/SizeCoverageBanner';
import { useShopify } from '../providers/AppBridgeProvider';
import { useLanguage } from '../providers/LanguageProvider';
import { useOnboarding } from '../onboarding/useOnboarding';
import { getThemeEditorUrl } from '../onboarding/format';
import ProgressiveHero from '../components/ProgressiveHero';
import type { PageName } from '../types';
import { PlanTier } from '../types';

// Exported so tests can drive the dismissed state directly rather than
// duplicating this literal.
export const ONBOARDING_DISMISSED_KEY = 'aisthetix-onboarding-dismissed';

/** Prices are quoted in USD on every locale */
function formatUsd(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

const VIDEO_URL = 'https://youtu.be/cqS_MuNUNJw';

// The guided flow is heavy (six step components) and most sessions never
// mount it, so it stays out of the main dashboard chunk.
const OnboardingFlow = lazy(() => import('../onboarding/OnboardingFlow'));

interface DashboardPageProps {
  onNavigate: (page: PageName) => void;
}

function isOnboardingDismissed(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function DashboardPage({ onNavigate }: DashboardPageProps) {
  const { subscription, loading: subLoading } = useSubscription();
  const { usage, loading: usageLoading } = useUsage();
  const { settings } = useSettings();
  const { compatible, themeName, loading: themeLoading } = useThemeCompatibility();
  const { shop } = useShopify();
  const { t, language } = useLanguage();
  const [dismissed, setDismissed] = useState(isOnboardingDismissed);
  const onboarding = useOnboarding();

  const loading = subLoading || usageLoading;
  // The server-persisted flow state decides what this page is. A failed
  // state endpoint falls open to the classic dashboard: onboarding must
  // never be the reason the admin is unusable.
  const flowState = onboarding.failed ? null : onboarding.state;
  const inGuidedFlow = !onboarding.loading && flowState?.status === 'active';
  const isNewFlowMerchant = flowState?.flowEntry === 'new_install';
  const dateLocale = language === 'it' ? 'it-IT' : 'en-US';
  const o = t.onboarding;
  const tc = t.themeCompatibility;

  // Show onboarding for new merchants (no subscription) always.
  // For subscribed merchants, allow dismiss via localStorage.
  const showOnboarding = !subscription || !dismissed;

  // A guide only counts as done when at least one of them can actually serve
  // a size. The engine refuses any basis other than `body`, so a shop with
  // guides that are all garment/unknown leaves the feature off: ticking this
  // step then would have the setup card telling the merchant they are set up
  // while the settings page tells them they are not.
  const sizeGuides = settings?.sizeGuides ?? [];
  const hasSizeGuide = sizeGuides.some((g) => g.usable ?? g.structuredSizeGuide.measurement_basis === 'body');
  const sizeGuideNotBodyBasis = sizeGuides.length > 0 && !hasSizeGuide;

  // Built once and placed above the setup card wherever it appears below:
  // a coverage problem is live operational news, not a setup step, so it
  // stays visible whether or not the merchant already dismissed onboarding.
  const sizeCoverageBanner = (
    <SizeCoverageBanner
      report={settings?.sizeCoverageReport ?? null}
      guides={sizeGuides}
      onGoToSettings={() => onNavigate('settings')}
    />
  );

  const themeEditorUrl = getThemeEditorUrl(shop);

  // --- Onboarding / setup card ---
  const renderThemeStatus = () => {
    if (themeLoading) {
      return (
        <Text as="p" variant="bodySm" tone="subdued">
          {tc.checking}
        </Text>
      );
    }
    if (compatible === false) {
      return (
        <InlineStack gap="100" align="start" blockAlign="center">
          <Icon source={AlertCircleIcon} tone="critical" />
          <Text as="p" variant="bodySm" tone="critical">
            {tc.incompatibleBody(themeName)}
          </Text>
        </InlineStack>
      );
    }
    if (compatible === true && themeName) {
      return (
        <InlineStack gap="100" align="start" blockAlign="center">
          <Icon source={CheckCircleIcon} tone="success" />
          <Text as="p" variant="bodySm" tone="success">
            {tc.compatible(themeName)}
          </Text>
        </InlineStack>
      );
    }
    return null;
  };

  const handleDismissOnboarding = () => {
    setDismissed(true);
    try {
      localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
    } catch {
      // Silently fail if localStorage is unavailable
    }
  };

  const onboardingCard = (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start">
          <BlockStack gap="100">
            <Text as="h2" variant="headingMd">
              {o.title}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {o.subtitle}
            </Text>
          </BlockStack>
          {subscription && (
            <Button variant="plain" onClick={handleDismissOnboarding}>
              {o.dismiss}
            </Button>
          )}
        </InlineStack>

        <Divider />

        {/* Step 1: Theme compatibility */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {o.step1Title}
          </Text>
          <Text as="p" variant="bodyMd">
            {o.step1Body}
          </Text>
          {renderThemeStatus()}
        </BlockStack>

        <Divider />

        {/* Step 2: Choose a plan */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {o.step2Title}
          </Text>
          <Text as="p" variant="bodyMd">
            {o.step2Body}
          </Text>
          {!subscription && (
            <Box>
              <Button onClick={() => onNavigate('subscription')} disabled={compatible === false}>
                {o.step2Action}
              </Button>
            </Box>
          )}
          {subscription && (
            <InlineStack gap="100" align="start" blockAlign="center">
              <Icon source={CheckCircleIcon} tone="success" />
              <Badge tone="success">{subscription.planTier}</Badge>
            </InlineStack>
          )}
        </BlockStack>

        <Divider />

        {/* Step 3: Add widget to theme */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {o.step3Title}
          </Text>
          <Text as="p" variant="bodyMd">
            {o.step3Body}
          </Text>
          <InlineStack gap="300" wrap>
            <Button
              url={themeEditorUrl}
              external
              disabled={!shop || compatible === false}
            >
              {o.step3Action}
            </Button>
            <Button
              url={VIDEO_URL}
              external
              variant="plain"
            >
              {o.watchVideo}
            </Button>
          </InlineStack>
        </BlockStack>

        <Divider />

        {/* Step 4: Configure */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {o.step4Title}
          </Text>
          <Text as="p" variant="bodyMd">
            {o.step4Body}
          </Text>
          <Box>
            <Button onClick={() => onNavigate('settings')}>
              {o.step4Action}
            </Button>
          </Box>
        </BlockStack>

        <Divider />

        {/* Step 5: Size guide, done only once it can actually serve a size. */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {o.step5Title}
          </Text>
          <Text as="p" variant="bodyMd">
            {settings?.garmentChartsEnabled === true ? o.step5GarmentBody : o.step5Body}
          </Text>
          {hasSizeGuide ? (
            <InlineStack gap="100" align="start" blockAlign="center">
              <Icon source={CheckCircleIcon} tone="success" />
              <Text as="p" variant="bodySm" tone="success">
                {o.step5Done}
              </Text>
            </InlineStack>
          ) : (
            <BlockStack gap="200">
              {sizeGuideNotBodyBasis && (
                <InlineStack gap="100" align="start" blockAlign="center">
                  <Icon source={AlertCircleIcon} tone="critical" />
                  <Text as="p" variant="bodySm" tone="critical">
                    {o.step5NotBodyBasis}
                  </Text>
                </InlineStack>
              )}
              <Box>
                <Button onClick={() => onNavigate('settings')}>{o.step5Action}</Button>
              </Box>
            </BlockStack>
          )}
        </BlockStack>

        <Divider />

        {/* Step 6: Targeting */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {o.step6Title}
          </Text>
          <Text as="p" variant="bodyMd">
            {o.step6Body}
          </Text>
          <Box>
            <Button onClick={() => onNavigate('settings')}>{o.step6Action}</Button>
          </Box>
        </BlockStack>
      </BlockStack>
    </Card>
  );

  // --- Guided onboarding replaces the whole dashboard while active ---
  if (inGuidedFlow) {
    return (
      <Suspense
        fallback={
          <Page title={t.dashboard.title}>
            <Layout>
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <SkeletonDisplayText size="small" />
                    <SkeletonBodyText lines={3} />
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        }
      >
        <OnboardingFlow onboarding={onboarding} />
      </Suspense>
    );
  }

  // --- No subscription state ---
  if (!loading && !onboarding.loading && !subscription) {
    return (
      <Page title={t.dashboard.title}>
        <Layout>
          {compatible === false && (
            <Layout.Section>
              <Banner title={tc.incompatibleTitle} tone="critical">
                <p>{tc.incompatibleBody(themeName)}</p>
              </Banner>
            </Layout.Section>
          )}
          <Layout.Section>
            <Banner
              title={t.dashboard.welcomeTitle}
              tone="info"
              action={{
                content: t.dashboard.choosePlan,
                onAction: () => onNavigate('subscription'),
                disabled: compatible === false,
              }}
            >
              <p>{t.dashboard.welcomeBody}</p>
            </Banner>
          </Layout.Section>
          {sizeCoverageBanner}
          <Layout.Section>{onboardingCard}</Layout.Section>
        </Layout>
      </Page>
    );
  }

  // --- Loading state ---
  if (loading || onboarding.loading) {
    return (
      <Page title={t.dashboard.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={3} />
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // --- Active subscription state ---
  const usagePercent =
    usage && usage.includedTryOns > 0
      ? (usage.usedTryOns / usage.includedTryOns) * 100
      : 0;
  const isFreePlan = subscription?.planTier === PlanTier.FREE;
  const showLegacySetupCard = !isNewFlowMerchant && showOnboarding;
  const quotaUsedUp = Boolean(usage && usage.remainingTryOns <= 0);
  // Overage keeps the store serving past the included quota, so an exhausted
  // quota is news, not an outage: it must not show the red "paused" banner.
  const overageCovering = Boolean(usage?.overageEnabled) && quotaUsedUp;

  return (
    <Page title={t.dashboard.title}>
      <Layout>
        {/* App Store review ask (renders only at the attributed-revenue moment) */}
        <ReviewPromptBanner source="dashboard" />

        {/* The trial's last stretch, with the way out of it. Top of the page
            rather than inside the usage card: a paused store is the news. */}
        <TrialQuotaBanner usage={usage} />

        {/* Post-onboarding progressive hero (new-flow merchants) */}
        {isNewFlowMerchant && (
          <ProgressiveHero
            onboarding={onboarding}
            usage={usage}
            onOpenAnalytics={() => onNavigate('analytics')}
          />
        )}

        {/* Optional entry into the dashboard demo for merchants who predate it */}
        {!isNewFlowMerchant && flowState && (
          <Layout.Section>
            <Banner
              title={t.progressiveDashboard.tryDemoTitle}
              tone="info"
              action={{
                content: t.progressiveDashboard.tryDemoAction,
                onAction: () => void onboarding.enter(),
              }}
            >
              <p>{t.progressiveDashboard.tryDemoBody}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Current Plan Banner */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineGrid columns={2} gap="400">
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">
                    {t.dashboard.currentPlan}
                  </Text>
                  <Text as="p" variant="headingLg">
                    {subscription?.planTier || 'N/A'}
                  </Text>
                </BlockStack>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">
                    {t.dashboard.status}
                  </Text>
                  <Badge
                    tone={subscription?.status?.toUpperCase() === 'ACTIVE' ? 'success' : 'info'}
                  >
                    {subscription?.status || t.dashboard.unknown}
                  </Badge>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Usage Summary */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.dashboard.usageSummary}
              </Text>
              {usage ? (
                <>
                  <Text as="p" variant="bodyMd">
                    {usage.isTrial && usage.trialCap !== null
                      ? t.dashboard.trialProgress(usage.usedTryOns, usage.trialCap)
                      : t.dashboard.tryOnsUsed(usage.usedTryOns, usage.includedTryOns)}
                  </Text>
                  <ProgressBar
                    progress={Math.min(usagePercent, 100)}
                    tone={usagePercent > 80 && !overageCovering ? 'critical' : 'success'}
                  />
                  {overageCovering && usage.overagePricePerTryOn !== null ? (
                    <Banner tone="info" title={t.dashboard.overageActive}>
                      <p>
                        {t.dashboard.overageActiveBody(
                          formatUsd(usage.overagePricePerTryOn),
                          formatUsd(usage.overageCappedAmount ?? 0)
                        )}
                      </p>
                    </Banner>
                  ) : usage.isTrial ? (
                    // A trial has its own banner above, which offers a way out
                    // instead of pointing at a plan list. Nothing here: the
                    // monthly-limit copy below would talk about a billing cycle
                    // the merchant has not reached yet.
                    null
                  ) : quotaUsedUp && isFreePlan ? (
                    <Banner
                      tone="critical"
                      title={t.dashboard.freePlanExhausted}
                      action={{ content: t.dashboard.upgradeNow, onAction: () => onNavigate('subscription') }}
                    >
                      <p>{t.dashboard.freePlanExhaustedBody(usage.includedTryOns)}</p>
                    </Banner>
                  ) : quotaUsedUp ? (
                    <Banner
                      tone="critical"
                      title={t.dashboard.quotaExhausted}
                      action={{ content: t.dashboard.upgradeNow, onAction: () => onNavigate('subscription') }}
                    >
                      <p>{t.dashboard.quotaExhaustedBody}</p>
                    </Banner>
                  ) : usagePercent > 80 ? (
                    <Banner
                      tone="warning"
                      action={{ content: t.dashboard.upgradeNow, onAction: () => onNavigate('subscription') }}
                    >
                      <p>{t.dashboard.approachingLimit(usage.remainingTryOns)}</p>
                    </Banner>
                  ) : isFreePlan ? (
                    <Banner
                      tone="info"
                      title={t.dashboard.freePlanTitle}
                      action={{ content: t.dashboard.upgradeNow, onAction: () => onNavigate('subscription') }}
                    >
                      <p>{t.dashboard.freePlanBody(usage.includedTryOns)}</p>
                    </Banner>
                  ) : null}
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t.dashboard.daysRemaining(usage.daysRemaining)}
                  </Text>
                </>
              ) : (
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t.dashboard.noUsageData}
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Actions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.dashboard.quickActions}
              </Text>
              <InlineGrid columns={2} gap="400">
                <Button onClick={() => onNavigate('subscription')}>
                  {t.dashboard.manageSubscription}
                </Button>
                <Button onClick={() => onNavigate('usage')}>
                  {t.dashboard.viewDetailedUsage}
                </Button>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Billing Period Info */}
        {usage && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t.dashboard.billingPeriod}
                </Text>
                <InlineGrid columns={2} gap="400">
                  <BlockStack gap="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t.dashboard.startDate}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {new Date(usage.billingPeriodStart).toLocaleDateString(dateLocale)}
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t.dashboard.endDate}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {new Date(usage.billingPeriodEnd).toLocaleDateString(dateLocale)}
                    </Text>
                  </BlockStack>
                </InlineGrid>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Onboarding / Setup Guide */}
        {sizeCoverageBanner}
        {showLegacySetupCard ? (
          <Layout.Section>{onboardingCard}</Layout.Section>
        ) : isNewFlowMerchant ? null : (
          <Layout.Section>
            <Button
              variant="plain"
              onClick={() => {
                try { localStorage.removeItem(ONBOARDING_DISMISSED_KEY); } catch { /* ignore */ }
                setDismissed(false);
              }}
            >
              {o.showSetupGuide}
            </Button>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
